/** Zeilenumbruch in widgetOrder - verhindert Nachruecken aus naechster Reihe beim Entfernen einer Karte. */
export const ROW_BREAK = "|";
const COLS_PER_ROW = 3;
/** Reihen-Array in flache Order mit ROW_BREAK zwischen Zeilen. */
export function rowsToOrder(rows: string[][]): string[] {
    return rows.flatMap((row, i) => (i === 0 ? row : [ROW_BREAK, ...row]));
}
/** Order ohne ROW_BREAK um Zeilenumbrueche ergaenzen (Migration alter gespeicherter Reihenfolge). */
export function insertRowBreaks(keys: string[]): string[] {
    const out: string[] = [];
    for (let i = 0; i < keys.length; i++) {
        if (i > 0 && i % COLS_PER_ROW === 0)
            out.push(ROW_BREAK);
        out.push(keys[i]);
    }
    return out;
}
/** Gruppiert Liste in Zeilen (getrennt durch ROW_BREAK). Keine Nachruecker beim Entfernen. */
export function groupByRowBreak(arr: string[], allWidgetKeys: string[]): string[][] {
    const rows: string[][] = [];
    let current: string[] = [];
    for (const x of arr) {
        if (x === ROW_BREAK) {
            if (current.length > 0)
                rows.push(current);
            current = [];
        }
        else if (allWidgetKeys.includes(x as string)) {
            current.push(x);
        }
    }
    if (current.length > 0)
        rows.push(current);
    return rows;
}
/**
 * Legt die gezogene Karte an einer Zielkarte ab — in derselben Zeile wie zeilenübergreifend.
 *
 * `insertBefore` entscheidet, ob sie vor oder hinter der Zielkarte landet — in derselben Zeile wie
 * zeilenübergreifend, damit das Ergebnis genau der beim Ziehen angezeigten Einfügemarke entspricht.
 * Ist eine der beiden Karten nicht in der Reihenfolge enthalten, bleibt alles unverändert.
 */
export function moveCardToTarget(order: string[], activeId: string, targetId: string, insertBefore: boolean): string[] {
    const rows = groupRowsForMove(order);
    const activeRowIndex = rows.findIndex((row) => row.includes(activeId));
    const targetRowIndex = rows.findIndex((row) => row.includes(targetId));
    if (activeRowIndex === -1 || targetRowIndex === -1 || activeId === targetId)
        return order;
    const newRows = rows.map((row) => [...row]);
    newRows[activeRowIndex] = newRows[activeRowIndex].filter((key) => key !== activeId);
    const targetColumnIndex = newRows[targetRowIndex].indexOf(targetId);
    newRows[targetRowIndex].splice(Math.max(0, insertBefore ? targetColumnIndex : targetColumnIndex + 1), 0, activeId);
    return rowsToOrder(newRows.filter((row) => row.length > 0));
}
/**
 * Legt die gezogene Karte als eigene, neue Zeile ab. `rowIndex` bezeichnet die Zeile, vor der
 * eingefügt wird. Wird die Ausgangszeile dadurch leer, fällt sie weg — und die Zielposition
 * rückt entsprechend nach.
 */
export function moveCardToNewRow(order: string[], activeId: string, rowIndex: number): string[] {
    const rows = groupRowsForMove(order);
    const activeRowIndex = rows.findIndex((row) => row.includes(activeId));
    if (activeRowIndex === -1)
        return order;
    const withoutActive = rows.map((row) => row.filter((key) => key !== activeId));
    const sourceRowBecameEmpty = withoutActive[activeRowIndex].length === 0;
    const adjustedIndex = sourceRowBecameEmpty && activeRowIndex < rowIndex ? rowIndex - 1 : rowIndex;
    const remaining = withoutActive.filter((row) => row.length > 0);
    const insertAt = Math.max(0, Math.min(adjustedIndex, remaining.length));
    return rowsToOrder([...remaining.slice(0, insertAt), [activeId], ...remaining.slice(insertAt)]);
}
/** Zeilen einer Reihenfolge, ohne Filterung auf bekannte Karten — für das Umsortieren. */
function groupRowsForMove(order: string[]): string[][] {
    const rows: string[][] = [];
    let current: string[] = [];
    for (const entry of order) {
        if (entry === ROW_BREAK) {
            if (current.length > 0)
                rows.push(current);
            current = [];
        }
        else {
            current.push(entry);
        }
    }
    if (current.length > 0)
        rows.push(current);
    return rows;
}
export function resolveWidgetOrderWithBreaks(widgetOrder: string[], allWidgetKeys: string[]): string[] {
    const raw = widgetOrder.length > 0 ? widgetOrder : allWidgetKeys.map((k) => String(k));
    const valid = raw.filter((x) => x === ROW_BREAK || allWidgetKeys.includes(x as string));
    const rest = allWidgetKeys.filter((k) => !valid.includes(k));
    if (rest.length === 0)
        return valid.includes(ROW_BREAK) ? valid : insertRowBreaks(valid);
    // Karten, die es beim letzten Speichern noch nicht gab, beginnen eine neue Zeile und werden
    // wie der Standard umbrochen. Ohne das landen sie alle in einer einzigen, endlos breiten Reihe —
    // bei einer groesseren Produkterweiterung sind das schnell ein Dutzend nebeneinander.
    return valid.includes(ROW_BREAK)
        ? [...valid, ROW_BREAK, ...insertRowBreaks(rest)]
        : insertRowBreaks([...valid, ...rest]);
}
